import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input } from "@/design-system/native";
import { DEFAULT_LABEL_PALETTE } from "../../utils/labels";
import styles from "./ManageLabelsModal.module.scss";

const ManageLabelsModal = ({
  isOpen,
  toggle,
  labels = [],
  setLabels,
  title = "Manage labels",
  palette = DEFAULT_LABEL_PALETTE,
}) => {
  const [name, setName] = useState("");
  const [selectedColor, setSelectedColor] = useState(palette[0]);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState("");

  const activeLabel = useMemo(
    () => labels.find((l) => l.id === selectedId),
    [labels, selectedId]
  );

  useEffect(() => {
    if (!isOpen) return;
    setError("");
  }, [isOpen]);

  useEffect(() => {
    if (!activeLabel) return;
    setName(activeLabel.name);
    setSelectedColor(activeLabel.color || palette[0]);
  }, [activeLabel, palette]);

  const clearSelection = () => {
    setSelectedId(null);
    setName("");
    setSelectedColor(palette[0]);
    setError("");
  };

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("This field is required.");
      return;
    }
    setError("");
    setLabels?.((prev) => {
      const byName = prev.find(
        (l) => l.name.toLowerCase() === trimmed.toLowerCase()
      );
      if (selectedId) {
        return prev.map((l) =>
          l.id === selectedId ? { ...l, name: trimmed, color: selectedColor } : l
        );
      }
      if (byName) {
        return prev.map((l) =>
          l.name.toLowerCase() === trimmed.toLowerCase()
            ? { ...l, color: selectedColor }
            : l
        );
      }
      return [
        ...prev,
        {
          id: `${trimmed.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}`,
          name: trimmed,
          color: selectedColor,
        },
      ];
    });
    clearSelection();
  };

  const handleDelete = () => {
    if (!selectedId) return;
    setLabels?.((prev) => prev.filter((l) => l.id !== selectedId));
    clearSelection();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <span className={styles.title}>{title}</span>
      </ModalHeader>
      <ModalBody>
        <div className={styles.swatchRow}>
          {palette.map((color, idx) => (
            <span
              key={`${color}-${idx}`}
              onClick={() => setSelectedColor(color)}
              className={`${styles.swatch} ${
                selectedColor === color ? styles["swatch--selected"] : ""
              }`}
              style={{ "--swatch-bg": color }}
            />
          ))}
        </div>

        <div className={styles.inputRow}>
          <Input
            value={name}
            placeholder="Label"
            onChange={(e) => setName(e.target.value)}
            className="app-input app-input--placeholder"
          />
          <Button color="primary" onClick={handleSave}>
            Save
          </Button>
        </div>
        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.pillRow}>
          {labels.map((label) => {
            const isActive = selectedId === label.id;
            return (
              <span
                key={label.id}
                onClick={() => setSelectedId(label.id)}
                className={`${styles.pill} ${
                  isActive ? styles["pill--active"] : ""
                }`}
              >
                <span
                  className={styles.pillDot}
                  style={{ "--dot-bg": label.color }}
                />
                {label.name}
              </span>
            );
          })}
        </div>
      </ModalBody>
      <ModalFooter className={styles.footer}>
        <div className={styles.footerGroup}>
          {selectedId && (
            <>
              <Button color="white" className={styles.lightBtn} onClick={handleDelete}>
                Delete
              </Button>
              <Button color="white" className={styles.lightBtn} onClick={clearSelection}>
                Cancel
              </Button>
            </>
          )}
        </div>
        <div className={styles.footerGroup}>
          <Button color="white" onClick={toggle} className={`${styles.lightBtn} ${styles.closeBtn}`}>
          Close
          </Button>
          <Button color="primary" onClick={handleSave}>
            Save
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ManageLabelsModal;
